import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Star, ChevronRight, QrCode, X } from "lucide-react";
import { SITE } from "@/config";

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, ease: "easeOut" },
};

export default function MarconiApp() {
  const [showQr, setShowQr] = useState(false);

  const openExternal = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const pageUrl = typeof window !== "undefined" ? window.location.origin + "/" : "";

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#FAF7F2]">
      <div
        className="pointer-events-none absolute inset-0 mx-auto max-w-md bg-cover bg-top bg-no-repeat"
        style={{ backgroundImage: "url('/bg-marconi.jpg')" }}
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-8 pt-7">
        {/* Logo */}
        <header className="flex shrink-0 justify-center">
          <img
            data-testid="logo-header"
            src={SITE.logo}
            alt="Spazio Marconi - Pizza Bruna e Cucina"
            className="h-24 w-auto object-contain mix-blend-multiply"
          />
        </header>

        <div className="flex flex-1 flex-col justify-center py-4">
          <motion.section {...fade} className="flex flex-col">
            <h1 className="font-display text-[40px] leading-[1.05] font-extrabold text-[#2D1E18]">
              {SITE.homeTitle}
            </h1>
            <span className="mt-5 block h-[3px] w-14 rounded-full bg-[#8B261D]" />
            <p className="mt-5 text-center text-[17px] leading-relaxed text-[#6b574d]">
              {SITE.homeSubtitle}
            </p>

            <div className="mt-8 space-y-4">
              {/* Google */}
              <button
                data-testid="google-review-button"
                onClick={() => openExternal(SITE.googleUrl)}
                className="group flex w-full items-center gap-3 rounded-[26px] bg-white/95 px-4 py-4 shadow-[0_12px_34px_rgba(139,38,29,0.10)] ring-1 ring-black/[0.04] backdrop-blur active:scale-[0.98] transition-transform duration-200"
              >
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-white shadow-[0_4px_14px_rgba(0,0,0,0.08)]">
                  <GoogleG />
                </span>
                <span className="flex-1 text-center">
                  <span className="block text-[17px] font-bold tracking-wide text-[#2D1E18]">
                    RECENSISCI SU GOOGLE
                  </span>
                  <span className="mt-1 flex items-center justify-center gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="h-4 w-4 fill-[#FBBC05] text-[#FBBC05]" />
                    ))}
                  </span>
                  <span className="mt-0.5 block text-[13px] text-[#8a746a]">
                    Lascia la tua recensione
                  </span>
                </span>
                <ChevronCircle />
              </button>

              {/* Tripadvisor */}
              <button
                data-testid="tripadvisor-review-button"
                onClick={() => openExternal(SITE.tripadvisorUrl)}
                className="group flex w-full items-center gap-3 rounded-[26px] bg-white/95 px-4 py-5 shadow-[0_12px_34px_rgba(139,38,29,0.10)] ring-1 ring-black/[0.04] backdrop-blur active:scale-[0.98] transition-transform duration-200"
              >
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#E4F4EC]">
                  <OwlEyes />
                </span>
                <span className="flex-1 text-center">
                  <span className="block text-[17px] font-bold tracking-wide text-[#2D1E18]">
                    RECENSISCI SU TRIPADVISOR
                  </span>
                  <span className="mt-0.5 block text-[13px] text-[#8a746a]">
                    Lascia la tua opinione
                  </span>
                </span>
                <ChevronCircle />
              </button>
            </div>

            <button
              data-testid="show-qr-button"
              onClick={() => setShowQr(true)}
              className="mx-auto mt-7 flex items-center gap-2 rounded-full border border-[#8B261D]/20 bg-white/60 px-4 py-2 text-[13px] font-medium text-[#8B261D] backdrop-blur active:scale-95 transition-transform"
            >
              <QrCode className="h-4 w-4" /> Mostra il QR di questa pagina
            </button>
          </motion.section>
        </div>

        <footer className="shrink-0 pt-6 text-center">
          <p className="font-display text-[15px] tracking-wide text-[#8B261D]">
            {SITE.restaurantName}
          </p>
          <span className="mx-auto my-2 block h-px w-10 bg-[#8B261D]/25" />
        </footer>
      </div>

      {/* QR modal */}
      <AnimatePresence>
        {showQr && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-6 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowQr(false)}
          >
            <motion.div
              data-testid="qr-modal"
              className="relative w-full max-w-xs rounded-3xl bg-[#FAF7F2] p-7 text-center shadow-2xl"
              initial={{ scale: 0.9, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 16 }}
              transition={{ duration: 0.22 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="qr-close-button"
                onClick={() => setShowQr(false)}
                className="absolute right-4 top-4 text-[#9a887e] active:scale-90 transition-transform"
              >
                <X className="h-5 w-5" />
              </button>
              <h2 className="font-display text-xl font-bold text-[#2D1E18]">
                Inquadra il codice
              </h2>
              <p className="mt-1.5 text-[13px] leading-relaxed text-[#6b574d]">
                Apri questa pagina anche senza NFC: inquadra il QR con la fotocamera.
              </p>
              <div className="mx-auto mt-5 w-fit rounded-2xl bg-white p-4 shadow-[0_8px_30px_rgba(139,38,29,0.10)]">
                <QRCodeSVG value={pageUrl} size={188} fgColor="#2D1E18" bgColor="#ffffff" level="M" />
              </div>
              <p className="mt-4 font-display text-[13px] tracking-wide text-[#8B261D]">
                Spazio Marconi
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ChevronCircle() {
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#f0ece5] group-active:bg-[#e6e0d7] transition-colors">
      <ChevronRight className="h-5 w-5 text-[#8a746a]" />
    </span>
  );
}

function OwlEyes() {
  return (
    <svg width="30" height="22" viewBox="0 0 48 34" aria-hidden="true">
      <circle cx="14" cy="17" r="11.5" fill="none" stroke="#00AF87" strokeWidth="3" />
      <circle cx="14" cy="17" r="4.2" fill="#00AF87" />
      <circle cx="34" cy="17" r="11.5" fill="none" stroke="#00AF87" strokeWidth="3" />
      <circle cx="34" cy="17" r="4.2" fill="#00AF87" />
      <path d="M18 30c2 2.6 8 2.6 10 0" fill="none" stroke="#00AF87" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

function GoogleG() {
  return (
    <svg width="26" height="26" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
